import { Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Python weekday ids (0 = Monday … 6 = Sunday), displayed Saturday first.
export const WEEK_ORDER = [[5, "السبت", "Sat"], [6, "الأحد", "Sun"], [0, "الاثنين", "Mon"], [1, "الثلاثاء", "Tue"], [2, "الأربعاء", "Wed"], [3, "الخميس", "Thu"], [4, "الجمعة", "Fri"]];

export function fmtTime(t, lang) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const h12 = h % 12 || 12;
  const pm = h >= 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${lang === "ar" ? (pm ? "م" : "ص") : pm ? "PM" : "AM"}`;
}

export function daysLabel(days, lang) {
  if (days.length === 7) return lang === "ar" ? "كل أيام الأسبوع" : "Every day";
  return WEEK_ORDER.filter(([id]) => days.includes(id)).map(([, ar, en]) => (lang === "ar" ? ar : en)).join(lang === "ar" ? "، " : ", ");
}

export const parseDays = (s) => String(s ?? "").split(",").filter((x) => x !== "").map(Number);

// Ad-level working hours stored in fields: work_days ("5,6,0"), work_from ("08:00"), work_to ("18:00").
export function adHoursSummary(fields, lang) {
  const days = parseDays(fields?.work_days);
  if (!days.length) return "";
  return `${daysLabel(days, lang)} · \u2066${fmtTime(fields.work_from, lang)} — ${fmtTime(fields.work_to, lang)}\u2069`;
}

const HOURS12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];
const split24 = (t) => { const [h, m] = (t || "08:00").split(":").map(Number); return { h12: String(h % 12 || 12).padStart(2, "0"), m: String(m).padStart(2, "0"), pm: h >= 12 }; };
const join24 = (h12, m, pm) => `${String((Number(h12) % 12) + (pm ? 12 : 0)).padStart(2, "0")}:${m}`;

function Part({ value, options, onChange, testid, render = (x) => x }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11 flex-1 min-w-0 rounded-xl border-[#E5E5E0] bg-[#FAF8F3] font-extrabold text-base justify-center gap-1 px-2" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-60">{options.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{render(o)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function TimeSelect({ label, value, onChange, lang, testid }) {
  const { h12, m, pm } = split24(value);
  const minutes = MINUTES.includes(m) ? MINUTES : [...MINUTES, m].sort();
  return (
    <div className="flex-1 min-w-0 rounded-2xl border border-[#E5E5E0] bg-white p-3" data-testid={testid}>
      <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#064E3B]" />{label}</p>
      <div className="flex items-center gap-1.5" dir="ltr">
        <Part value={h12} options={HOURS12} onChange={(h) => onChange(join24(h, m, pm))} testid={`${testid}-hour`} />
        <span className="font-extrabold text-slate-400">:</span>
        <Part value={m} options={minutes} onChange={(mm) => onChange(join24(h12, mm, pm))} testid={`${testid}-minute`} />
      </div>
      <div className="grid grid-cols-2 gap-1.5 mt-2">
        {[[false, "ص", "AM"], [true, "م", "PM"]].map(([isPm, ar, en]) => (
          <button key={en} type="button" onClick={() => onChange(join24(h12, m, isPm))} data-testid={`${testid}-${en.toLowerCase()}`}
            className={`h-9 rounded-lg text-sm font-bold border transition-colors ${pm === isPm ? "bg-[#064E3B] border-[#064E3B] text-white" : "bg-white border-[#E5E5E0] text-slate-600"}`}>{lang === "ar" ? ar : en}</button>
        ))}
      </div>
      <p className="text-center text-xs font-semibold text-[#064E3B] mt-2" dir="ltr" data-testid={`${testid}-value`}>{fmtTime(value, lang)}</p>
    </div>
  );
}

// value: { days: number[], start: "HH:MM", end: "HH:MM" }
export function HoursPicker({ value, onChange, L, lang, testid = "hours", onSave }) {
  const toggle = (d) => onChange({ ...value, days: value.days.includes(d) ? value.days.filter((x) => x !== d) : [...value.days, d] });
  return (
    <div className="space-y-4" data-testid={`${testid}-picker`}>
      <p className="text-xs leading-relaxed text-slate-500 bg-[#F4F8F5] border border-[#1A5C3A]/15 rounded-xl px-3 py-2" data-testid={`${testid}-note`}>
        {L("لن تظهر للعملاء إلا الأيام والأوقات التي تحددها هنا، وتُحجب الأوقات المحجوزة تلقائياً", "Customers only see the days and times you set here; booked times are blocked automatically")}
      </p>
      <div>
        <p className="text-xs font-bold text-slate-500 mb-1.5">{L("أيام العمل", "Working days")}</p>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {WEEK_ORDER.map(([id, ar, en]) => {
            const on = value.days.includes(id);
            return (
              <button key={id} type="button" onClick={() => toggle(id)} data-testid={`${testid}-day-${id}`}
                className={`h-11 rounded-xl border-2 text-sm font-bold transition-colors ${on ? "bg-[#064E3B] border-[#064E3B] text-white" : "bg-slate-100 border-slate-200 text-slate-500 hover:border-[#064E3B]"}`}>{L(ar, en)}</button>
            );
          })}
        </div>
      </div>
      <div className="flex gap-3">
        <TimeSelect label={L("من الساعة", "From")} value={value.start} onChange={(start) => onChange({ ...value, start })} lang={lang} testid={`${testid}-start`} />
        <TimeSelect label={L("حتى الساعة", "Until")} value={value.end} onChange={(end) => onChange({ ...value, end })} lang={lang} testid={`${testid}-end`} />
      </div>
      {onSave && (
        <button type="button" onClick={onSave} data-testid={`${testid}-save`}
          className="w-full h-12 rounded-xl bg-[#064E3B] text-white font-extrabold text-[15px] hover:bg-[#053d2f] transition-colors">{L("حفظ المواعيد", "Save schedule")}</button>
      )}
    </div>
  );
}
